import { User, LogOut, House } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocation } from "wouter";
import { WaggleLogo } from "@/components/waggle/WaggleLogo";
import { useAuth } from "@/context/AuthContext";
import { useTest } from "@/context/TestContext";

interface HeaderProps {
  pageTitle?: string;
  showUser?: boolean;
}

export function Header({ pageTitle, showUser = true }: HeaderProps) {
  const [location, setLocation] = useLocation();
  // The /create/* wizard is a focused, full-screen task (no sidebar), so the
  // way out reads as leaving it. The draft autosaves, so exiting is safe.
  const inWizard = location.startsWith("/create");
  const { logout } = useAuth();
  const { resetTest } = useTest();

  // Clear the session before leaving — Login bounces signed-in users straight
  // back to the dashboard.
  const handleLogout = async () => {
    resetTest();
    await logout();
    setLocation("/");
  };

  const handleDashboard = () => {
    setLocation("/dashboard");
  };

  return (
    <header className="h-16 border-b bg-background flex items-center justify-between px-6">
      <a
        href="/dashboard"
        onClick={(e) => {
          e.preventDefault();
          handleDashboard();
        }}
        aria-label="Waggle home"
        className="rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <WaggleLogo />
      </a>

      {pageTitle && (
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <h2 className="text-base font-medium text-foreground">{pageTitle}</h2>
        </div>
      )}

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          onClick={handleDashboard}
          title={inWizard ? "Your progress is saved — pick it up from Home anytime." : undefined}
          data-testid="button-home"
        >
          {inWizard ? "Exit to Home" : "Back to Home"} <House />
        </Button>

        {showUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="rounded-full"
                data-testid="button-user-menu"
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDashboard} data-testid="menu-dashboard">
                <House className="mr-2 h-4 w-4" />
                Home
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
